import { z } from 'zod';
import { accessGate, conditionGroup, configKey, editableText, publishable } from './primitives.js';

/**
 * MOTOR DE CAMPOS
 *
 * O cadastro de lote (Cap. 6 a 10), o cadastro de procura (Cap. 12.2) e os
 * cadastros de anunciante (Cap. 3) NAO sao formularios escritos a mao. Sao
 * documentos `FormSchema` editados no painel. Adicionar "Poco artesiano" ao
 * cadastro do SoFazenda e uma operacao de administrador, nao de deploy.
 */

export const fieldType = z.enum([
  // texto
  'text',
  'textarea',
  'richtext',
  // numerico
  'number',
  'currency', // armazenado em centavos
  'area', // m2 / ha, ver `unit`
  'measure', // metros lineares (frente, fundos, laterais)
  'percent',
  // escolha
  'select',
  'multiselect',
  'radio',
  'checkbox',
  'boolean',
  'tags',
  // temporal
  'date',
  'datetime',
  // identidade / contato brasileiro
  'cpf',
  'cnpj',
  'creci',
  'password',
  'phone',
  'whatsapp',
  'email',
  'url',
  // localizacao
  'cep',
  'address',
  'geopoint',
  'state',
  'city',
  'neighborhood',
  // midia
  'image',
  'gallery',
  'video',
  'document',
  // estrutura
  'section',
  'repeater',
  // derivado — nunca editavel pelo usuario final
  'computed',
]);

export type FieldType = z.infer<typeof fieldType>;

/** Opcao de um campo de escolha. */
export const fieldOption = z.object({
  value: z.string().min(1),
  label: editableText,
  /** Nome de icone do design system, opcional. */
  icon: z.string().nullable().default(null),
  description: editableText.nullable().default(null),
  enabled: z.boolean().default(true),
  order: z.number().int().default(0),
});

export type FieldOption = z.infer<typeof fieldOption>;

/**
 * Origem das opcoes. `static` vive na propria configuracao; `taxonomy` aponta
 * para uma lista mantida em /admin/taxonomias (topografia, infraestrutura,
 * aptidao, documentacao...); `remote` consulta o backend (cidades, bairros).
 */
export const optionSource = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('static'), options: z.array(fieldOption).default([]) }),
  z.object({ kind: z.literal('taxonomy'), taxonomy: configKey }),
  z.object({
    kind: z.literal('remote'),
    endpoint: z.string().min(1),
    /** Campos do formulario cujos valores viram query-string, ex.: cidade -> bairros. */
    dependsOn: z.array(configKey).default([]),
    labelPath: z.string().default('label'),
    valuePath: z.string().default('value'),
  }),
]);

export type OptionSource = z.infer<typeof optionSource>;

export const fieldValidation = z.object({
  required: z.boolean().default(false),
  min: z.number().nullable().default(null),
  max: z.number().nullable().default(null),
  minLength: z.number().int().nullable().default(null),
  maxLength: z.number().int().nullable().default(null),
  /** Regex serializada. O painel valida no momento de salvar. */
  pattern: z.string().nullable().default(null),
  /**
   * Exige que o valor seja igual ao de outro campo. Usado na confirmacao de
   * senha e de e-mail — a checagem depende de dois campos, entao nao cabe na
   * validacao isolada de um deles.
   */
  matchesField: configKey.nullable().default(null),
  /** Torna o campo obrigatorio apenas quando as condicoes baterem. */
  requiredWhen: conditionGroup.nullable().default(null),
  /** Mensagem que o admin escreve; sem ela o front usa a mensagem padrao. */
  message: editableText.nullable().default(null),
});

export type FieldValidation = z.infer<typeof fieldValidation>;

/** Como o campo participa da busca publica (Cap. 20) e do match (Cap. 12). */
export const fieldIndexing = z.object({
  /** Entra no indice de texto livre. */
  searchable: z.boolean().default(false),
  /** Vira filtro na listagem. */
  filterable: z.boolean().default(false),
  /** Vira faceta com contagem. */
  facetable: z.boolean().default(false),
  /** Pode ordenar a listagem. */
  sortable: z.boolean().default(false),
  /** Peso do campo no match. 0 = nao participa. */
  matchWeight: z.number().min(0).max(10).default(0),
});

export type FieldIndexing = z.infer<typeof fieldIndexing>;

/** Onde o valor aparece depois de publicado. */
export const fieldPresentation = z.object({
  showInCard: z.boolean().default(false),
  showInHeader: z.boolean().default(false),
  showInDetail: z.boolean().default(true),
  /** Largura no grid de 12 colunas do formulario. */
  span: z.number().int().min(1).max(12).default(12),
  placeholder: editableText.nullable().default(null),
  help: editableText.nullable().default(null),
  prefix: z.string().nullable().default(null),
  suffix: z.string().nullable().default(null),
  /**
   * Nome de um componente registrado em `fieldRegistry`. Escotilha de fuga
   * para o campo raro que o motor generico nao cobre — o unico ponto em que
   * o desenvolvedor volta a ser necessario.
   */
  widget: z.string().nullable().default(null),
});

export type FieldPresentation = z.infer<typeof fieldPresentation>;

/**
 * Campo derivado. Cobre "valor por m2 calculado automaticamente" (Cap. 6.3)
 * sem uma linha de codigo dedicada.
 */
export const computedSpec = z.object({
  /** Expressao aritmetica restrita: identificadores, numeros, + - * / ( ). */
  formula: z.string().min(1),
  /** Casas decimais na exibicao. */
  precision: z.number().int().min(0).max(6).default(2),
});

export type ComputedSpec = z.infer<typeof computedSpec>;

const fieldBase = z.object({
  key: configKey,
  type: fieldType,
  label: editableText,
  source: optionSource.nullable().default(null),
  validation: fieldValidation.default({}),
  indexing: fieldIndexing.default({}),
  presentation: fieldPresentation.default({}),
  computed: computedSpec.nullable().default(null),
  /** Visibilidade condicional dentro do formulario. */
  visibleWhen: conditionGroup.nullable().default(null),
  /** Quem enxerga/edita este campo (por plano ou perfil). */
  gate: accessGate.default({ roles: [], plans: [] }),
  /** Unidade para area/measure/currency: 'm2' | 'ha' | 'm' | 'BRL'. */
  unit: z.string().nullable().default(null),
  /**
   * Campo do dominio ao qual este campo mapeia. Vazio = atributo dinamico,
   * gravado em JSONB. Preenchido = coluna forte da tabela de anuncios.
   */
  mapsTo: z.string().nullable().default(null),
  /** Campos sensiveis nunca vao no payload publico (Cap. 7.2 / LGPD). */
  sensitive: z.boolean().default(false),
  enabled: z.boolean().default(true),
  order: z.number().int().default(0),
});

export type FieldSchema = z.infer<typeof fieldBase> & {
  children?: FieldSchema[];
};

/**
 * O que o painel envia: os `.default()` tornam quase tudo opcional na
 * entrada. Precisa ser um tipo separado do de saida, senao o zod nao
 * consegue fechar a recursao.
 */
export type FieldSchemaInput = z.input<typeof fieldBase> & {
  children?: FieldSchemaInput[];
};

/** `section` e `repeater` aninham campos — por isso o z.lazy. */
export const fieldSchema: z.ZodType<FieldSchema, z.ZodTypeDef, FieldSchemaInput> = fieldBase.extend({
  children: z.lazy(() => z.array(fieldSchema)).optional(),
});

export const formStep = z.object({
  key: configKey,
  title: editableText,
  description: editableText.nullable().default(null),
  fields: z.array(fieldSchema).default([]),
  visibleWhen: conditionGroup.nullable().default(null),
});

export type FormStep = z.infer<typeof formStep>;

export const formSchema = z.object({
  key: configKey,
  version: z.number().int().min(1),
  title: editableText,
  description: editableText.nullable().default(null),
  /** Passos do wizard. Um passo so = formulario unico. */
  steps: z.array(formStep).default([]),
  /** Declaracoes de aceite exigidas ao enviar (Cap. 9.3). */
  acknowledgements: z
    .array(
      z.object({
        key: configKey,
        text: editableText,
        required: z.boolean().default(true),
        documentSlug: z.string().nullable().default(null),
      }),
    )
    .default([]),
  publication: publishable.default({}),
});

export type FormSchema = z.infer<typeof formSchema>;

/** Percorre a arvore de campos em profundidade. */
export function walkFields(fields: FieldSchema[], visit: (field: FieldSchema, path: string[]) => void): void {
  const walk = (nodes: FieldSchema[], path: string[]) => {
    for (const node of nodes) {
      visit(node, path);
      if (node.children?.length) walk(node.children, [...path, node.key]);
    }
  };
  walk(fields, []);
}

export function flattenFields(schema: FormSchema): FieldSchema[] {
  const out: FieldSchema[] = [];
  for (const step of schema.steps) walkFields(step.fields, (field) => out.push(field));
  return out;
}
